import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import {
  ArrowLeftIcon, DocumentTextIcon, BanknotesIcon, BuildingLibraryIcon,
} from '@heroicons/react/24/outline';
import { employeeApi } from '../../../services/api';
import { PageHeader, Spinner, EmptyState } from '../../../components/shared/index';
import TaxProfileForm from './TaxProfileForm';
import DeductionsList from './DeductionsList';
import BankAccountsList from './BankAccountsList';

const TABS = [
  { key: 'tax',       label: 'Tax Profile',    icon: DocumentTextIcon },
  { key: 'deductions',label: 'Deductions',     icon: BanknotesIcon },
  { key: 'banks',     label: 'Bank Accounts',  icon: BuildingLibraryIcon },
];

export default function EmployeePayroll() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState('tax');

  const { data: employee, isLoading } = useQuery({
    queryKey: ['employee', id],
    queryFn:  () => employeeApi.get(id).then(r => r.data),
    enabled:  !!id,
  });

  if (isLoading) return <div className="flex justify-center p-20"><Spinner size="lg" /></div>;

  if (!employee) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <EmptyState
          title="Employee not found"
          description="This employee may have been archived."
          action={<button onClick={() => navigate('/app/employees')} className="btn-primary">Back to employees</button>}
        />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <Link
        to={`/app/employees/${id}`}
        className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-3"
      >
        <ArrowLeftIcon className="w-4 h-4" /> Back to employee
      </Link>

      <PageHeader
        title={`${employee.name} · Payroll`}
        subtitle={[
          employee.job_title,
          employee.role && employee.role.replace(/_/g, ' '),
          employee.department?.name,
        ].filter(Boolean).join(' · ')}
      />

      <div className="border-b border-gray-200 mb-6">
        <nav className="flex gap-1">
          {TABS.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={clsx(
                'flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px',
                tab === key
                  ? 'border-blue-600 text-blue-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700',
              )}
            >
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
        </nav>
      </div>

      {tab === 'tax'        && <TaxProfileForm employeeId={id} />}
      {tab === 'deductions' && <DeductionsList employeeId={id} />}
      {tab === 'banks'      && <BankAccountsList employeeId={id} />}
    </div>
  );
}
